<script setup>
import { ref, computed } from 'vue'
import TableList from '../others/TableList.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Commons from '@/shared/utils/commons'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'

const statusTriggerRef = ref(null)

const STATUS_LABELS = {
  pending:   'En attente',
  paid:      'Payé',
  cancelled: 'Annulé',
  delivered: 'Livré',
  expired:   'Expiré',
}

const STATUS_CLASSES = {
  pending:   'bg-amber-50 text-amber-600 border-amber-200',
  paid:      'bg-green-50 text-green-600 border-green-200',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
  delivered: 'bg-blue-50 text-blue-600 border-blue-200',
  expired:   'bg-gray-50 text-gray-500 border-gray-200',
}

const NEXT_STATUS_OPTIONS = {
  pending:   [{ label: 'Marquer payé', value: 'paid' }, { label: 'Annuler', value: 'cancelled' }],
  paid:      [{ label: 'Marquer livré', value: 'delivered' }],
  delivered: [],
  cancelled: [],
  expired:   [],
}

const onUpdateStatus = async (item, newStatus) => {
  const label = STATUS_LABELS[newStatus] || newStatus
  
  const res = await window.customPrompt({
    title: `Mettre à jour le statut ?`,
    description: `Voulez-vous mettre à jour le statut de l'achat de "${item.productName}" vers "${label}" ?`,
    formFields: [],
    successText: 'Mettre à jour',
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    statusTriggerRef.value?.trigger({ id: item.id, status: newStatus })
  }
}

const onSuccess = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Succès', body: 'Statut mis à jour avec succès', type: 'success' })
}
const onError = (err) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Erreur', body: err?.message || 'Une erreur est survenue', type: 'error' })
}

const cols = computed(() => [
  {
    name: 'Date',
    id: 'createdAt',
    value(el) {
      return `<span class="text-sm text-gray-600">${el.createdAt ? Commons.time(el.createdAt) : '—'}</span>`
    },
  },
  {
    name: 'Acheteur',
    id: 'buyerName',
    filter: {
      key: 'buyerType',
      default: '',
      options: [
        { name: 'Tous', value: '' },
        { name: 'Livreur', value: 'rider' },
        { name: 'Restaurant', value: 'restaurant' },
      ],
    },
    value(el) {
      const badge = el.buyerType === 'rider'
        ? `<span class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-600">Livreur</span>`
        : `<span class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-600">Restaurant</span>`
      const phone = el.phone ? `<span class="text-xs text-gray-400">${el.phone}</span>` : ''
      return [
        `${badge} ${el.buyerName || '—'}`,
        el.phone || '',
      ]
    },
  },
  {
    name: 'Produit',
    id: 'productName',
    value(el) {
      return `<span class="font-medium text-sm text-gray-800">${el.productName || '—'}</span>`
    },
  },
  {
    name: 'Qté',
    id: 'quantity',
    value(el) {
      return `<span class="text-sm font-semibold text-gray-700 text-center block">${el.quantity}</span>`
    },
  },
  {
    name: 'Montant',
    id: 'totalPrice',
    value(el) {
      const unit = el.quantity > 1
        ? `<div class="text-xs text-gray-400">${el.quantity} × ${Commons.formatAmount(el.unitPrice)}</div>`
        : ''
      return `<div class="text-right"><div class="text-sm font-bold text-gray-900">${Commons.formatAmount(el.totalPrice)} ${el.currency}</div>${unit}</div>`
    },
  },
  {
    name: 'Statut',
    id: 'status',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tous', value: '' },
        { name: 'En attente', value: 'pending' },
        { name: 'Payé', value: 'paid' },
        { name: 'Livré', value: 'delivered' },
        { name: 'Annulé', value: 'cancelled' },
        { name: 'Expiré', value: 'expired' },
      ],
    },
    value(el) {
      const cls = STATUS_CLASSES[el.status] || 'bg-gray-50 text-gray-500 border-gray-200'
      const label = STATUS_LABELS[el.status] || el.status || '—'
      return `<span class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${cls}">${label}</span>`
    },
  },
  {
    name: 'Action',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options(el) {
      const nexts = NEXT_STATUS_OPTIONS[el.status] || []
      if (nexts.length === 0) return [{ name: '—', value: null }]
      return nexts.map(opt => ({ name: opt.label, value: opt.value }))
    },
    onClick(opt, el) {
      if (opt?.value) onUpdateStatus(el, opt.value)
    },
    value() {},
  },
])
</script>

<template>
  <div class="p-5 h-full overflow-auto">
    <TableList
      title="Ventes de Produits Rapidoo"
      dataKey="adminProductPurchases"
      hasSearch
      :cols="cols"
      :refreshOnEvents="['PURCHASES_UPDATED']"
    />

    <ActionTrigger
      dataKey="updatePurchaseStatus"
      ref="statusTriggerRef"
      @success="onSuccess"
      @failed="onError"
      dispatchOnSuccess="PURCHASES_UPDATED"
    />
  </div>
</template>
